import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, Modal, Row, Space, Table, Tooltip, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import MomentDatePicker from "../../Components/MomentDatePicker";
import { createOil, getAllOil, updateOil } from "../../shared/api/Oil";
import { dateFormat, dateFormatYMD } from "../../shared/dataFormat";
import { useRequestLock } from "../../shared/hooks/useRequestLock";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";

const { Title } = Typography;

const formLayout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

export const OilPriceTab = () => {
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalUpdateVisible, setIsModalUpdateVisible] = useState(false);
  const [id, setId] = useState("");
  const [oilPrice, setOilPrice] = useState({
    list: [],
    loading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const [form] = Form.useForm();
  const [formUpdate] = Form.useForm();
  const { isLocked, runLocked } = useRequestLock();
  const createSaveKey = "oil-price:create";
  const updateSaveKey = `oil-price:${id}`;
  const isCreating = isLocked(createSaveKey);
  const isUpdating = isLocked(updateSaveKey);

  const fetchData = async () => {
    setOilPrice((prev) => ({ ...prev, loading: true }));
    const response: any = await getAllOil({
      params: {
        current: oilPrice.pagination.current,
        pageSize: oilPrice.pagination.pageSize,
      },
    });
    setOilPrice((prev) => ({
      ...prev,
      list: response?.data ?? [],
      loading: false,
      pagination: {
        ...prev.pagination,
        total: response?.total ?? response?.totalItem ?? 0,
      },
    }));
  };

  useEffect(() => {
    fetchData();
  }, [oilPrice.pagination.current, oilPrice.pagination.pageSize]);

  const handleOk = () => {
    if (isLocked(createSaveKey)) return;
    form.submit();
  };

  const closeCreateModal = () => {
    form.resetFields();
    setIsModalVisible(false);
  };
  const handleCancel = () => {
    if (isLocked(createSaveKey)) return;
    closeCreateModal();
  };

  const handleOkUpdate = () => {
    if (isLocked(updateSaveKey)) return;
    formUpdate.submit();
  };

  const closeUpdateModal = () => {
    formUpdate.resetFields();
    setIsModalUpdateVisible(false);
  };
  const handleCancelUpdate = () => {
    if (isLocked(updateSaveKey)) return;
    closeUpdateModal();
  };

  const onCreate = async () => {
    await runLocked(createSaveKey, async () => {
      const response = await createOil({
        ...form.getFieldsValue(),
        date: dateFormatYMD(form.getFieldsValue()?.date),
      });
      if (response.success) {
        closeCreateModal();
        await fetchData();
      }
    });
  };

  const onUpdate = async () => {
    await runLocked(updateSaveKey, async () => {
      const response = await updateOil(id, {
        ...formUpdate.getFieldsValue(),
        date: dateFormatYMD(formUpdate.getFieldsValue()?.date),
      });
      if (response.success) {
        closeUpdateModal();
        await fetchData();
      }
    });
  };

  const columns: Array<object> = [
    {
      title: "STT",
      width: 50,
      render: (_id: any, _record: any, index: number) => index + 1,
    },
    {
      title: "Ngày",
      dataIndex: "date",
      render: (value: any) => dateFormat(value),
    },
    {
      title: "Giá dầu",
      dataIndex: "price",
      render: (value: any) => `${new Intl.NumberFormat().format(value)}VNĐ`,
    },
    {
      title: "Tác vụ",
      width: 80,
      render: (oil: any) => (
        <Space size="middle">
          <Tooltip title="Sửa">
            <Button
              type="text"
              disabled={!checkPermissions("FUEL", "edit", features)}
              onClick={() => {
                setId(oil._id);
                setIsModalUpdateVisible(true);
                formUpdate.setFieldsValue({
                  date: moment(oil.date),
                  price: oil.price,
                });
              }}
            >
              <EditOutlined style={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Tạo giá dầu"
        open={isModalVisible}
        confirmLoading={isCreating}
        closable={!isCreating}
        maskClosable={!isCreating}
        keyboard={!isCreating}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{
          disabled:
            isCreating || !checkPermissions("FUEL", "add", features),
        }}
        cancelButtonProps={{ disabled: isCreating }}
      >
        <Form {...formLayout} form={form} onFinish={onCreate}>
          <Form.Item name="price" label="Giá dầu">
            <InputNumber
              formatter={(value) =>
                ` ${value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="date" label="Ngày">
            <MomentDatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Cập nhật giá dầu"
        open={isModalUpdateVisible}
        confirmLoading={isUpdating}
        closable={!isUpdating}
        maskClosable={!isUpdating}
        keyboard={!isUpdating}
        onOk={handleOkUpdate}
        onCancel={handleCancelUpdate}
        okButtonProps={{
          disabled:
            isUpdating || !checkPermissions("FUEL", "edit", features),
        }}
        cancelButtonProps={{ disabled: isUpdating }}
      >
        <Form form={formUpdate} onFinish={onUpdate}>
          <Form.Item name="price" label="Giá dầu">
            <InputNumber
              formatter={(value) =>
                ` ${value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="date" label="Ngày">
            <MomentDatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
      <Row justify="space-between" gutter={6}>
        <Title level={4}>Quản lý giá dầu</Title>
        <Space size={6}>
          <Button
            onClick={() => {
              setIsModalVisible(true);
            }}
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            disabled={!checkPermissions("FUEL", "add", features)}
          >
            Tạo mới
          </Button>
        </Space>
      </Row>
      <br />
      <Table
        scroll={{ x: "max-content" }}
        rowKey="_id"
        loading={oilPrice.loading}
        columns={columns}
        pagination={
          oilPrice.pagination.total > oilPrice.pagination.pageSize &&
          oilPrice.pagination
        }
        dataSource={oilPrice.list}
        onChange={(value: any) =>
          setOilPrice((prev) => ({
            ...prev,
            pagination: { ...prev.pagination, current: value.current },
          }))
        }
      />
    </>
  );
};
